import { UIMetadataComplete } from '../../../types/UIMetadata';

/**
 * Groove Configurator (linkId 2386).
 *
 * UI half of the 2.0 metadata pair — every key mirrors a configurationId in
 * ./graphics-metadata.ts. Converted from the 1.0 repo's
 * `src/metadata/data/metadata-2386.ts`; shared UI boilerplate
 * (staticTranslations / saveFormConfig) follows the 2378 project.
 *
 * Conversion notes:
 *  - 1.0 `removeAccessories` + `defaultAccessory` become a shared graphics
 *    `groupId` + `isDefault` (2.0's single-select mechanism), namespaced per
 *    trim because the SDK resolves groupIds model-wide.
 *  - 1.0 `forColor` (allow-list of colour indices) is inverted into 2.0's
 *    `notAvailableForColor` deny-list of colour ids.
 *  - `interactionApplyMessage`/`applyInteractions` become
 *    `affectedInteractionOnApply`; `includedPackages` become
 *    `dependencyPackages`; `brandItemCode` becomes `autoIntelRPOCode`.
 *  - Dropped, with no 2.0 equivalent: `video` Welcome Animation interactions,
 *    feature `tempSkuToLoad`/`replaceAsset`, `interactionRemoveMessage`,
 *    interaction `meshAnimation`/`glowMesh`.
 *  - Broken references in the 1.0 source (dangling ids, duplicate accessory
 *    ids, identifiers naming assets the variant never loads) were repaired —
 *    see conversion-report-2386.txt.
 *
 * Assets live in public/assets/{images,videos}/2386/.
 */
// Cast through unknown to allow additional model-level UI fields (e.g., name, specifications)
export const uimetadata = {
  models: {
    Groove: {
      variants: {
        'rs-fwd': {
          configurationId: 'u5qm0qtkgk60',
          name: {
            en: 'RS',
            ar: 'RS'
          },
          description: {
            en: 'FWD',
            ar: 'الدفع الأمامي'
          },
          drivetrain: 'FWD',
          trim: 'RS',
          image: 'images/2386/variant_RS.png',
          price: {
            KSA: 75408,
            UAE: 74000,
            OMR: 7842,
            BHD: 7641,
            QAR: 73196,
            JOD: 14277,
            KW: 6234,
            IQD: 20109,
            LBP: 20109,
            default: 74000,
          },
          basePrice: {
            UAE: 74000,
            KSA: 85200,
            KW: 5870,
            JOD: 25100,
            QAR: 69800,
            BHD: 8170,
            OMR: 7750,
            IQD: 0,
            LBP: 0,
            default: 74000
          },
          index: 0,
          isDefault: true,
          ar: {
            enable: false,
            redirection: false
          },
          autoIntelRPOCode: '1SC',
          highlights: [],
          specs: [],
          toggleTabs: {
            CONFIG: {
              mobile: false,
              desktop: false
            },
            MODEL: {
              mobile: true,
              desktop: true
            },
            EXTERIOR: {
              mobile: true,
              desktop: true
            },
            INTERIOR: {
              mobile: true,
              desktop: true
            },
            OPTIONS: {
              mobile: false,
              desktop: false
            },
            SUMMARY: {
              mobile: true,
              desktop: true
            }
          },
          brandItemCode: 'CHGR01',
        },
        'lt-fwd': {
          configurationId: 'u5qfgm0qtkgk60',
          name: {
            en: 'LT',
            ar: 'LT'
          },
          description: {
            en: 'FWD',
            ar: 'الدفع الأمامي'
          },
          drivetrain: 'FWD',
          trim: 'LT',
          image: 'images/2386/variant_LT_1SB.png',
          price: {
            KSA: 65217,
            UAE: 64000,
            OMR: 6783,
            BHD: 6609,
            QAR: 63304,
            JOD: 12348,
            KW: 5391,
            IQD: 17391,
            LBP: 17391,
            default: 64000,
          },
          basePrice: {
            UAE: 64000,
            KSA: 73700,
            KW: 5070,
            JOD: 21700,
            QAR: 60300,
            BHD: 7060,
            OMR: 6700,
            IQD: 0,
            LBP: 0,
            default: 64000
          },
          index: 1,
          isDefault: false,
          ar: {
            enable: false,
            redirection: false
          },
          autoIntelRPOCode: '1SA',
          highlights: [],
          specs: [],
          toggleTabs: {
            CONFIG: {
              mobile: false,
              desktop: false
            },
            MODEL: {
              mobile: true,
              desktop: true
            },
            EXTERIOR: {
              mobile: true,
              desktop: true
            },
            INTERIOR: {
              mobile: true,
              desktop: true
            },
            OPTIONS: {
              mobile: false,
              desktop: false
            },
            SUMMARY: {
              mobile: true,
              desktop: true
            }
          },
          brandItemCode: 'CHGR02',
        }
      },
      backgrounds: {
        g0ZA968_RpUd: {
          image: 'images/2386/hdri_2.png',
          title: {
            en: 'City Landscape 1',
            ar: 'غروب الشمس على الشاطئ'
          }
        },
        g0ZA968_RpUe: {
          image: 'images/2386/hdri_1.png',
          title: {
            en: 'City Landscape 2',
            ar: 'غروب الشمس على الشاطئ'
          }
        }
      },
      ambientLighting: {},
      colors: {
        interior: {
          bmbnnt678r0o: {
            label: {
              en: 'Sports black leatherette seats with Red stitching',
              ar: 'مقاعد رياضية من الجلد باللون الأسود مع خياطة باللون الأحمر'
            },
            image: 'images/2386/RS_Black.jpg',
            usdz: 'interior_red',
            price: {
              KSA: 0,
              UAE: 0,
              OMR: 0,
              BHD: 0,
              QAR: 0,
              JOD: 0,
              default: 0,
            },
            priceBreakdown: [],
            index: 0,
            type: 'interior',
            cardType: 'cart',
            cartImage: '',
            hasQuantity: false,
            autoIntelRPOCode: 'EZ1',
            brandItemCode: 'IN23',
          },
          'bmbnnt678r0o-2': {
            label: {
              en: 'Grey Cloth',
              ar: 'قماش باللون الرمادي'
            },
            image: 'images/2386/LT_1SB_Grey.jpg',
            usdz: 'interior_red',
            price: {
              KSA: 0,
              UAE: 0,
              OMR: 0,
              BHD: 0,
              QAR: 0,
              JOD: 0,
              default: 0,
            },
            priceBreakdown: [],
            index: 0,
            type: 'interior',
            cardType: 'cart',
            cartImage: '',
            hasQuantity: false,
            autoIntelRPOCode: 'EPV',
            brandItemCode: 'IN22',
          }
        },
        exterior: {
          k550ufl2z63j: {
            label: {
              en: 'Track Yellow',
              ar: 'أصفر'
            },
            image: '',
            hex: '#EAC117',
            usdz: 'red_black',
            price: {
              KSA: 0,
              UAE: 0,
              OMR: 0,
              BHD: 0,
              QAR: 0,
              JOD: 0,
              KW: 0,
              IQD: 0,
              LBP: 0,
              default: 0,
            },
            priceBreakdown: [
              {
                title: {
                  en: 'Exterior Color',
                  ar: 'اللون الخارجي'
                },
                label: {
                  en: 'Track Yellow',
                  ar: 'أصفر'
                },
                price: {
                  KSA: 0,
                  UAE: 0,
                  OMR: 0,
                  BHD: 0,
                  QAR: 0,
                  JOD: 0,
                  KW: 0,
                  IQD: 0,
                  LBP: 0,
                  default: 0,
                }
              }
            ],
            index: 0,
            type: 'exterior',
            cardType: 'cart',
            cartImage: '',
            hasQuantity: false,
            autoIntelRPOCode: '98U',
            brandItemCode: 'EX43',
          },
          'k550ufl2z63j-2': {
            label: {
              en: 'Track Yellow',
              ar: 'أصفر'
            },
            image: '',
            hex: '#EAC117',
            usdz: 'red_black',
            price: {
              KSA: 0,
              UAE: 0,
              OMR: 0,
              BHD: 0,
              QAR: 0,
              JOD: 0,
              KW: 0,
              IQD: 0,
              LBP: 0,
              default: 0,
            },
            priceBreakdown: [
              {
                title: {
                  en: 'Exterior Color',
                  ar: 'اللون الخارجي'
                },
                label: {
                  en: 'Track Yellow',
                  ar: 'أصفر'
                },
                price: {
                  KSA: 0,
                  UAE: 0,
                  OMR: 0,
                  BHD: 0,
                  QAR: 0,
                  JOD: 0,
                  KW: 0,
                  IQD: 0,
                  LBP: 0,
                  default: 0,
                }
              }
            ],
            index: 0,
            type: 'exterior',
            cardType: 'cart',
            cartImage: '',
            hasQuantity: false,
            autoIntelRPOCode: '98U',
            brandItemCode: 'EX43',
          },
          gxqxsxnaljfj: {
            label: {
              en: 'Star Twinkle Black',
              ar: 'أسود'
            },
            image: '',
            hex: '#141414',
            usdz: 'red',
            price: {
              KSA: 0,
              UAE: 0,
              OMR: 0,
              BHD: 0,
              QAR: 0,
              JOD: 0,
              KW: 0,
              IQD: 0,
              LBP: 0,
              default: 0,
            },
            priceBreakdown: [
              {
                title: {
                  en: 'Exterior Color',
                  ar: 'طلاء أحمر مشع'
                },
                label: {
                  en: 'Star Twinkle Black',
                  ar: 'أسود'
                },
                price: {
                  KSA: 0,
                  UAE: 0,
                  OMR: 0,
                  BHD: 0,
                  QAR: 0,
                  JOD: 0,
                  KW: 0,
                  IQD: 0,
                  LBP: 0,
                  default: 0,
                }
              }
            ],
            index: 1,
            type: 'exterior',
            cardType: 'cart',
            cartImage: '',
            hasQuantity: false,
            autoIntelRPOCode: 'GBA',
            brandItemCode: 'EX37',
          },
          'gxqxsxnaljfj-2': {
            label: {
              en: 'Star Twinkle Black',
              ar: 'أسود'
            },
            image: '',
            hex: '#141414',
            usdz: 'red',
            price: {
              KSA: 0,
              UAE: 0,
              OMR: 0,
              BHD: 0,
              QAR: 0,
              JOD: 0,
              KW: 0,
              IQD: 0,
              LBP: 0,
              default: 0,
            },
            priceBreakdown: [
              {
                title: {
                  en: 'Exterior Color',
                  ar: 'طلاء أحمر مشع'
                },
                label: {
                  en: 'Star Twinkle Black',
                  ar: 'أسود'
                },
                price: {
                  KSA: 0,
                  UAE: 0,
                  OMR: 0,
                  BHD: 0,
                  QAR: 0,
                  JOD: 0,
                  KW: 0,
                  IQD: 0,
                  LBP: 0,
                  default: 0,
                }
              }
            ],
            index: 1,
            type: 'exterior',
            cardType: 'cart',
            cartImage: '',
            hasQuantity: false,
            autoIntelRPOCode: 'GBA',
            brandItemCode: 'EX37',
          },
          '9i25yo15msgi': {
            label: {
              en: 'Pearl White',
              ar: 'أبيض لؤلؤي'
            },
            image: '',
            hex: '#F6F5F0',
            usdz: 'black',
            price: {
              KSA: 0,
              UAE: 0,
              OMR: 0,
              BHD: 0,
              QAR: 0,
              JOD: 0,
              KW: 0,
              IQD: 0,
              LBP: 0,
              default: 0,
            },
            priceBreakdown: [
              {
                title: {
                  en: 'Exterior Color',
                  ar: 'اللون الخارجي'
                },
                label: {
                  en: 'Pearl White',
                  ar: 'أبيض لؤلؤي'
                },
                price: {
                  KSA: 0,
                  UAE: 0,
                  OMR: 0,
                  BHD: 0,
                  QAR: 0,
                  JOD: 0,
                  KW: 0,
                  IQD: 0,
                  LBP: 0,
                  default: 0,
                }
              }
            ],
            index: 2,
            type: 'exterior',
            cardType: 'cart',
            cartImage: '',
            hasQuantity: false,
            autoIntelRPOCode: 'GBN',
            brandItemCode: 'EX44',
          },
          '9i25yo15msgi-2': {
            label: {
              en: 'Pearl White',
              ar: 'أبيض لؤلؤي'
            },
            image: '',
            hex: '#F6F5F0',
            usdz: 'black',
            price: {
              KSA: 0,
              UAE: 0,
              OMR: 0,
              BHD: 0,
              QAR: 0,
              JOD: 0,
              KW: 0,
              IQD: 0,
              LBP: 0,
              default: 0,
            },
            priceBreakdown: [
              {
                title: {
                  en: 'Exterior Color',
                  ar: 'اللون الخارجي'
                },
                label: {
                  en: 'Pearl White',
                  ar: 'أبيض لؤلؤي'
                },
                price: {
                  KSA: 0,
                  UAE: 0,
                  OMR: 0,
                  BHD: 0,
                  QAR: 0,
                  JOD: 0,
                  KW: 0,
                  IQD: 0,
                  LBP: 0,
                  default: 0,
                }
              }
            ],
            index: 2,
            type: 'exterior',
            cardType: 'cart',
            cartImage: '',
            hasQuantity: false,
            autoIntelRPOCode: 'GBN',
            brandItemCode: 'EX44',
          },
          l415icrduyfe: {
            label: {
              en: 'Grey',
              ar: 'رمادي'
            },
            image: '',
            hex: '#5C6062',
            usdz: 'blue',
            price: {
              KSA: 0,
              UAE: 0,
              OMR: 0,
              BHD: 0,
              QAR: 0,
              JOD: 0,
              KW: 0,
              IQD: 0,
              LBP: 0,
              default: 0,
            },
            priceBreakdown: [
              {
                title: {
                  en: 'Exterior Color',
                  ar: 'اللون الخارجي'
                },
                label: {
                  en: 'Grey',
                  ar: 'رمادي'
                },
                price: {
                  KSA: 0,
                  UAE: 0,
                  OMR: 0,
                  BHD: 0,
                  QAR: 0,
                  JOD: 0,
                  KW: 0,
                  IQD: 0,
                  LBP: 0,
                  default: 0,
                }
              }
            ],
            index: 3,
            type: 'exterior',
            cardType: 'cart',
            cartImage: '',
            hasQuantity: false,
            autoIntelRPOCode: 'GIR',
            brandItemCode: 'EX45',
          },
          'l415icrduyfe-2': {
            label: {
              en: 'Grey',
              ar: 'رمادي'
            },
            image: '',
            hex: '#5C6062',
            usdz: 'blue',
            price: {
              KSA: 0,
              UAE: 0,
              OMR: 0,
              BHD: 0,
              QAR: 0,
              JOD: 0,
              KW: 0,
              IQD: 0,
              LBP: 0,
              default: 0,
            },
            priceBreakdown: [
              {
                title: {
                  en: 'Exterior Color',
                  ar: 'اللون الخارجي'
                },
                label: {
                  en: 'Grey',
                  ar: 'رمادي'
                },
                price: {
                  KSA: 0,
                  UAE: 0,
                  OMR: 0,
                  BHD: 0,
                  QAR: 0,
                  JOD: 0,
                  KW: 0,
                  IQD: 0,
                  LBP: 0,
                  default: 0,
                }
              }
            ],
            index: 3,
            type: 'exterior',
            cardType: 'cart',
            cartImage: '',
            hasQuantity: false,
            autoIntelRPOCode: 'GIR',
            brandItemCode: 'EX45',
          },
          t0ah6d0ze4xe: {
            label: {
              en: 'Glaze Red',
              ar: 'أحمر'
            },
            image: '',
            hex: '#A61C26',
            usdz: 'blue',
            price: {
              KSA: 0,
              UAE: 0,
              OMR: 0,
              BHD: 0,
              QAR: 0,
              JOD: 0,
              KW: 0,
              IQD: 0,
              LBP: 0,
              default: 0,
            },
            priceBreakdown: [
              {
                title: {
                  en: 'Exterior Color',
                  ar: 'اللون الخارجي'
                },
                label: {
                  en: 'Glaze Red',
                  ar: 'أحمر'
                },
                price: {
                  KSA: 0,
                  UAE: 0,
                  OMR: 0,
                  BHD: 0,
                  QAR: 0,
                  JOD: 0,
                  KW: 0,
                  IQD: 0,
                  LBP: 0,
                  default: 0,
                }
              }
            ],
            index: 4,
            type: 'exterior',
            cardType: 'cart',
            cartImage: '',
            hasQuantity: false,
            autoIntelRPOCode: 'GNK',
            brandItemCode: 'EX46',
          },
          't0ah6d0ze4xe-2': {
            label: {
              en: 'Glaze Red',
              ar: 'أحمر'
            },
            image: '',
            hex: '#A61C26',
            usdz: 'blue',
            price: {
              KSA: 0,
              UAE: 0,
              OMR: 0,
              BHD: 0,
              QAR: 0,
              JOD: 0,
              KW: 0,
              IQD: 0,
              LBP: 0,
              default: 0,
            },
            priceBreakdown: [
              {
                title: {
                  en: 'Exterior Color',
                  ar: 'اللون الخارجي'
                },
                label: {
                  en: 'Glaze Red',
                  ar: 'أحمر'
                },
                price: {
                  KSA: 0,
                  UAE: 0,
                  OMR: 0,
                  BHD: 0,
                  QAR: 0,
                  JOD: 0,
                  KW: 0,
                  IQD: 0,
                  LBP: 0,
                  default: 0,
                }
              }
            ],
            index: 4,
            type: 'exterior',
            cardType: 'cart',
            cartImage: '',
            hasQuantity: false,
            autoIntelRPOCode: 'GNK',
            brandItemCode: 'EX46',
          },
          ew6nnknehod1: {
            label: {
              en: 'Tiger Blue',
              ar: 'أزرق'
            },
            image: '',
            hex: '#1D4273',
            usdz: 'blue',
            price: {
              KSA: 0,
              UAE: 0,
              OMR: 0,
              BHD: 0,
              QAR: 0,
              JOD: 0,
              KW: 0,
              IQD: 0,
              LBP: 0,
              default: 0,
            },
            priceBreakdown: [
              {
                title: {
                  en: 'Exterior Color',
                  ar: 'اللون الخارجي'
                },
                label: {
                  en: 'Tiger Blue',
                  ar: 'أزرق'
                },
                price: {
                  KSA: 0,
                  UAE: 0,
                  OMR: 0,
                  BHD: 0,
                  QAR: 0,
                  JOD: 0,
                  KW: 0,
                  IQD: 0,
                  LBP: 0,
                  default: 0,
                }
              }
            ],
            index: 5,
            type: 'exterior',
            cardType: 'cart',
            cartImage: '',
            hasQuantity: false,
            autoIntelRPOCode: 'GR1',
            brandItemCode: 'EX47',
          },
          'ew6nnknehod1-2': {
            label: {
              en: 'Tiger Blue',
              ar: 'أزرق'
            },
            image: '',
            hex: '#1D4273',
            usdz: 'blue',
            price: {
              KSA: 0,
              UAE: 0,
              OMR: 0,
              BHD: 0,
              QAR: 0,
              JOD: 0,
              KW: 0,
              IQD: 0,
              LBP: 0,
              default: 0,
            },
            priceBreakdown: [
              {
                title: {
                  en: 'Exterior Color',
                  ar: 'اللون الخارجي'
                },
                label: {
                  en: 'Tiger Blue',
                  ar: 'أزرق'
                },
                price: {
                  KSA: 0,
                  UAE: 0,
                  OMR: 0,
                  BHD: 0,
                  QAR: 0,
                  JOD: 0,
                  KW: 0,
                  IQD: 0,
                  LBP: 0,
                  default: 0,
                }
              }
            ],
            index: 5,
            type: 'exterior',
            cardType: 'cart',
            cartImage: '',
            hasQuantity: false,
            autoIntelRPOCode: 'GR1',
            brandItemCode: 'EX47',
          }
        }
      },
      features: {
        ylam62d7djz9: {
          label: {
            en: 'New Front Facia',
            ar: 'واجهة أمامية جديدة'
          },
          image: 'images/2386/RS_Bumper.jpg',
          title: {
            en: 'New Front Facia',
            ar: 'واجهة أمامية جديدة'
          },
          desc: {
            en: 'New fascia, Bumpers And Grille',
            ar: 'واجهة أمامية جديدة مع مصدات وشبك أمامي جديد'
          },
          type: 'exterior'
        },
        'ylam62d7djz9-2': {
          label: {
            en: 'New Front Facia',
            ar: 'واجهة أمامية جديدة'
          },
          image: 'images/2386/LT_1SB_Bumper.jpg',
          title: {
            en: 'New Front Facia',
            ar: 'واجهة أمامية جديدة'
          },
          desc: {
            en: 'New fascia, Bumpers And Grille',
            ar: 'واجهة أمامية جديدة مع مصدات وشبك أمامي جديد'
          },
          type: 'exterior'
        },
        kh1xk6tsachv: {
          label: {
            en: 'Black Bowties',
            ar: 'شعارات شفروليه باللون الأسود'
          },
          image: 'images/2386/RS_Front_Bowtie.jpg',
          title: {
            en: 'Black Bowties',
            ar: 'شعارات شفروليه باللون الأسود'
          },
          desc: {
            en: '',
            ar: ''
          },
          type: 'exterior'
        },
        'kh1xk6tsachv-2': {
          label: {
            en: 'Black Bowties',
            ar: 'شعارات شفروليه باللون الأسود'
          },
          image: 'images/2386/LT_1SB_Front_Bowtie.jpg',
          title: {
            en: 'Black Bowties',
            ar: 'شعارات شفروليه باللون الأسود'
          },
          desc: {
            en: '',
            ar: ''
          },
          type: 'exterior'
        },
        '1slkxz7syxvp': {
          label: {
            en: '17” Alloy Wheels',
            ar: 'عجلات ألومنيوم قياس 17 إنش'
          },
          image: 'images/2386/RS_Alloy.jpg',
          title: {
            en: '17” Alloy Wheels',
            ar: 'عجلات ألومنيوم قياس 17 إنش'
          },
          desc: {
            en: '',
            ar: ''
          },
          type: 'exterior'
        },
        '1slkxz7syxvp-2': {
          label: {
            en: '16” Alloy Wheels',
            ar: 'عجلات ألومنيوم قياس 16 إنش'
          },
          image: 'images/2386/LT_1SB_Alloy.jpg',
          title: {
            en: '16” Alloy Wheels',
            ar: 'عجلات ألومنيوم قياس 16 إنش'
          },
          desc: {
            en: '',
            ar: ''
          },
          type: 'exterior'
        },
        kbns5f4ez323: {
          label: {
            en: 'LED Front Headlamps',
            ar: 'مصابيح أمامية LED'
          },
          image: 'images/2386/RS_Headlmaps.jpg',
          title: {
            en: 'LED Front Headlamps',
            ar: 'مصابيح أمامية LED'
          },
          desc: {
            en: '',
            ar: ''
          },
          type: 'exterior'
        },
        'kbns5f4ez323-2': {
          label: {
            en: 'Halogen Front Headlamps',
            ar: 'مصابيح الهالوجين الأمامية'
          },
          image: 'images/2386/LT_1SB_Headlmaps.jpg',
          title: {
            en: 'Halogen Front Headlamps',
            ar: 'مصابيح الهالوجين الأمامية'
          },
          desc: {
            en: '',
            ar: ''
          },
          type: 'exterior'
        },
        k1gebz2rhrfs: {
          label: {
            en: 'Two-Tone Black Roof',
            ar: 'سقف أسود ثنائي اللون'
          },
          image: 'images/2386/RS_Roof.jpg',
          title: {
            en: 'Two-Tone Black Roof',
            ar: 'سقف أسود ثنائي اللون'
          },
          desc: {
            en: '',
            ar: ''
          },
          type: 'exterior'
        },
        '511hi24a2yfb': {
          label: {
            en: 'Sunroof',
            ar: 'فتحة سقف'
          },
          image: 'images/2386/RS_Roof.jpg',
          title: {
            en: 'Sunroof',
            ar: 'فتحة سقف'
          },
          desc: {
            en: '',
            ar: ''
          },
          type: 'exterior'
        },
        dk4jj9hgugds: {
          label: {
            en: 'Roof Rack',
            ar: 'حامل أمتعة علوي'
          },
          image: 'images/2386/RS_Roof.jpg',
          title: {
            en: 'Roof Rack',
            ar: 'حامل أمتعة علوي'
          },
          desc: {
            en: '',
            ar: ''
          },
          type: 'exterior'
        },
        y6zcqp9r8fln: {
          label: {
            en: 'Infoscreen',
            ar: 'شاشة المعلومات'
          },
          image: 'images/2386/RS_Infoscreen.jpg',
          title: {
            en: 'Infoscreen',
            ar: 'شاشة المعلومات'
          },
          desc: {
            en: '10.25" infotainment touchscreen',
            ar: 'نظام معلوماتي ترفيهي بشاشة لمس قياس 10.25 إنش'
          },
          type: 'interior'
        },
        'y6zcqp9r8fln-2': {
          label: {
            en: 'Infoscreen',
            ar: 'شاشة المعلومات'
          },
          image: 'images/2386/LT_1SB_Infoscreen.jpg',
          title: {
            en: 'Infoscreen',
            ar: 'شاشة المعلومات'
          },
          desc: {
            en: '10.25" infotainment touchscreen',
            ar: 'نظام معلوماتي ترفيهي بشاشة لمس قياس 10.25 إنش'
          },
          type: 'interior'
        },
        xsdac8nojc1s: {
          label: {
            en: 'DIC',
            ar: 'مركز معلومات القيادة'
          },
          image: 'images/2386/RS_Speedometer.jpg',
          title: {
            en: 'DIC',
            ar: 'مركز معلومات القيادة'
          },
          desc: {
            en: '7” Driver Information Center',
            ar: 'شاشة مركز معلومات القيادة قياس 7 إنش'
          },
          type: 'interior'
        },
        'xsdac8nojc1s-2': {
          label: {
            en: 'DIC',
            ar: 'مركز معلومات القيادة'
          },
          image: 'images/2386/LT_1SB_Speedometer.jpg',
          title: {
            en: 'DIC',
            ar: 'مركز معلومات القيادة'
          },
          desc: {
            en: '3.5” Driver Information Center',
            ar: 'شاشة مركز معلومات القيادة قياس 3.5 إنش'
          },
          type: 'interior'
        },
        ddlwz0n6pzn8: {
          label: {
            en: 'Seats',
            ar: 'المقاعد'
          },
          image: 'images/2386/RS_Seats.jpg',
          title: {
            en: 'Seats',
            ar: 'المقاعد'
          },
          desc: {
            en: 'Sporty leatherette seats with red stitching',
            ar: 'مقاعد جلد بطابع رياضي مع خياطة حمراء'
          },
          type: 'interior'
        },
        'ddlwz0n6pzn8-2': {
          label: {
            en: 'Seats',
            ar: 'المقاعد'
          },
          image: 'images/2386/LT_1SB_Seats.jpg',
          title: {
            en: 'Seats',
            ar: 'المقاعد'
          },
          desc: {
            en: 'Cloth Seats',
            ar: 'مقاعد قماشية'
          },
          type: 'interior'
        }
      },
      accessories: {
        wdbc64xihtb2: {
          label: {
            en: 'EXT_RS_Variant',
            ar: ''
          },
          image: '',
          category: {
            en: 'Packages',
            ar: 'الباقات'
          },
          price: {
            KSA: 0,
            UAE: 0,
            OMR: 0,
            BHD: 0,
            QAR: 0,
            JOD: 0,
            default: 0,
          },
          view: 'exterior',
          cardType: 'cart',
          hasQuantity: false
        },
        wdbqc64xihtb2: {
          label: {
            en: 'EXT_LT_Variant',
            ar: ''
          },
          image: '',
          category: {
            en: 'Packages',
            ar: 'الباقات'
          },
          price: {
            KSA: 0,
            UAE: 0,
            OMR: 0,
            BHD: 0,
            QAR: 0,
            JOD: 0,
            default: 0,
          },
          view: 'exterior',
          cardType: 'cart',
          hasQuantity: false
        },
        wdblc644xihtb2: {
          label: {
            en: 'Alloy_PWS_17_inch',
            ar: ''
          },
          image: '',
          category: {
            en: 'Packages',
            ar: 'الباقات'
          },
          price: {
            KSA: 0,
            UAE: 0,
            OMR: 0,
            BHD: 0,
            QAR: 0,
            JOD: 0,
            default: 0,
          },
          view: 'exterior',
          cardType: 'cart',
          hasQuantity: false
        },
        wdblc064x8ihtb2: {
          label: {
            en: 'Electronic_Brake',
            ar: ''
          },
          image: '',
          category: {
            en: 'Packages',
            ar: 'الباقات'
          },
          price: {
            KSA: 0,
            UAE: 0,
            OMR: 0,
            BHD: 0,
            QAR: 0,
            JOD: 0,
            default: 0,
          },
          view: 'exterior',
          cardType: 'cart',
          hasQuantity: false
        }
      },
      packages: {},
      interactions: {
        xbdzchklx04d: {
          label: {
            en: 'Doors',
            ar: 'الأبواب'
          },
          type: 'interaction'
        },
        '9a4ewd0iqpwk': {
          label: {
            en: 'Boot',
            ar: 'صندوق المركبة'
          },
          type: 'interaction'
        },
        fwou2afhghpp: {
          label: {
            en: 'Sunroof',
            ar: 'فتحة السقف'
          },
          type: 'interaction'
        },
        bkr8amy2tvbs: {
          label: {
            en: 'Headlight',
            ar: 'المصابيح الأمامية'
          },
          type: 'interaction'
        },
        '6146uzbd6kui': {
          label: {
            en: 'DRL',
            ar: 'مصابيح القيادة النهارية'
          },
          type: 'interaction'
        },
        iq7p37hq4g0j: {
          label: {
            en: 'Taillight',
            ar: 'المصباح الخلفي'
          },
          type: 'interaction'
        },
        z1338jyjvioh: {
          label: {
            en: 'Fog Lamps',
            ar: 'مصابيح الضباب'
          },
          type: 'interaction'
        }
      },
      hotspots: {
        exteriorCamera: {
          title: {
            en: 'Exterior',
            ar: 'الخارج'
          }
        },
        interiorCamera: {
          title: {
            en: 'First Row Seat',
            ar: 'مقعد الصف الأول'
          }
        },
        interiorCamera_1: {
          title: {
            en: 'Second Row Seat',
            ar: 'مقعد الصف الثاني'
          }
        }
      },
      name: {
        en: 'Chevrolet Groove',
        ar: 'شفروليه جرووف'
      },
      specifications: {
        heading: {
          en: 'Chevrolet Groove',
          ar: 'شفروليه جرووف'
        },
        trim: {
          en: '',
          ar: ''
        },
        price: {
          en: '',
          ar: ''
        },
        model: 'Chevrolet Groove',
        seatingCapacity: '5',
        dimensions: {
          length: '4365 mm',
          width: '',
          height: '',
          wheelbase: '2550 mm',
          groundClearance: ''
        },
        performance: [
          {
            value: '145',
            unit: 'HP',
            label: {
              en: '',
              ar: ''
            }
          },
          {
            value: '255',
            unit: 'NM',
            label: {
              en: 'Torque',
              ar: 'عزم الدوران'
            }
          },
          {
            value: '1.5L',
            unit: 'Turbo',
            label: {
              en: 'Engine',
              ar: 'المحرك'
            }
          }
        ],
        summary: [
          {
            title: {
              en: 'Model',
              ar: 'الطراز'
            },
            subtitle: {
              en: 'Chevrolet Groove',
              ar: 'شفروليه جرووف'
            },
            price: {
              KSA: 0,
              UAE: 0,
              OMR: 0,
              BHD: 0,
              QAR: 0,
              JOD: 0,
              default: 0,
            }
          },
          {
            title: {
              en: 'Exterior Colors',
              ar: 'الألوان الخارجية'
            },
            subtitle: {
              en: 'Track Yellow, Star Twinkle Black, Pearl White, Grey, Glaze Red, Tiger Blue (2-Tone Black Roof for RS)',
              ar: 'أصفر، أسود، أبيض لؤلؤي، رمادي، أحمر، أزرق (مع سقف أسود ثنائي اللون لطراز RS)'
            },
            price: {
              KSA: 0,
              UAE: 0,
              OMR: 0,
              BHD: 0,
              QAR: 0,
              JOD: 0,
              default: 0,
            }
          },
          {
            title: {
              en: 'Interior',
              ar: 'المقصورة'
            },
            subtitle: {
              en: 'Sports black leatherette seats with Red stitching (RS), Grey Cloth (LT)',
              ar: 'مقاعد رياضية من الجلد باللون الأسود مع خياطة باللون الأحمر (RS)، ومقاعد قماشية باللون الرمادي (LT)'
            },
            price: {
              KSA: 0,
              UAE: 0,
              OMR: 0,
              BHD: 0,
              QAR: 0,
              JOD: 0,
              default: 0,
            }
          },
          {
            title: {
              en: 'Wheelbase',
              ar: 'قاعدة العجلات'
            },
            subtitle: {
              en: '2550 mm',
              ar: '2550 مم'
            },
            price: {
              KSA: 0,
              UAE: 0,
              OMR: 0,
              BHD: 0,
              QAR: 0,
              JOD: 0,
              default: 0,
            }
          },
          {
            title: {
              en: 'Length',
              ar: 'الطول'
            },
            subtitle: {
              en: '4365 mm',
              ar: '4365 mm'
            },
            price: {
              KSA: 0,
              UAE: 0,
              OMR: 0,
              BHD: 0,
              QAR: 0,
              JOD: 0,
              default: 0,
            }
          },
          {
            title: {
              en: 'Cargo Space',
              ar: 'مساحة التخزين'
            },
            subtitle: {
              en: '1120 liters',
              ar: '1,120 لتر'
            },
            price: {
              KSA: 0,
              UAE: 0,
              OMR: 0,
              BHD: 0,
              QAR: 0,
              JOD: 0,
              default: 0,
            }
          },
          {
            title: {
              en: 'Transmission',
              ar: 'ناقل الحركة'
            },
            subtitle: {
              en: 'CVT',
              ar: 'ناقل حركة CVT'
            },
            price: {
              KSA: 0,
              UAE: 0,
              OMR: 0,
              BHD: 0,
              QAR: 0,
              JOD: 0,
              default: 0,
            }
          },
          {
            title: {
              en: 'Seating Capacity',
              ar: 'سعة المقاعد'
            },
            subtitle: {
              en: '5',
              ar: '5'
            },
            price: {
              KSA: 0,
              UAE: 0,
              OMR: 0,
              BHD: 0,
              QAR: 0,
              JOD: 0,
              default: 0,
            }
          },
          {
            title: {
              en: 'Infotainment Screen',
              ar: 'شاشة المعلومات والترفيه'
            },
            subtitle: {
              en: '10.25" Screen with Apple CarPlay® and Android Auto™ compatibility',
              ar: 'نظام معلوماتي ترفيهي بشاشة قياس 10.25 إنش مع دعم ®Apple CarPlay و™Android Auto'
            },
            price: {
              KSA: 0,
              UAE: 0,
              OMR: 0,
              BHD: 0,
              QAR: 0,
              JOD: 0,
              default: 0,
            }
          }
        ]
      }
    }
  },
  plugins: {
    deferredAssetLoading: true,
  },
  staticTranslations: {
    specDetails: {
      model: {
        en: 'Model',
        ar: 'نموذج',
      },
      exteriorColours: {
        en: 'Exterior',
        ar: 'الخارجة',
      },
      interiorAmbience: {
        en: 'Interior',
        ar: 'الداخلية',
      },
      seatingCapacity: {
        en: 'Seating Capacity',
        ar: 'سعة المقاعد',
      },
      length: {
        en: 'Length',
        ar: 'الطول',
      },
      width: {
        en: 'Width',
        ar: 'العرض',
      },
      height: {
        en: 'Height',
        ar: 'الارتفاع',
      },
      wheelbase: {
        en: 'Wheelbase',
        ar: 'قاعدة العجلات',
      },
      groundClearance: {
        en: 'Ground Clearance',
        ar: 'الخلوص الأرضي',
      },
      share: {
        en: 'Share',
        ar: 'يشارك',
      },
    },
    variantSelection: {
      title: {
        en: 'Model',
        ar: 'نموذج',
      },
      select: {
        en: 'Select',
        ar: 'تحديد',
      },
      cancel: {
        en: 'Cancel',
        ar: 'إلغاء',
      },
      selectBodyStyle: { en: 'Select Body Style', ar: 'اختر نمط الهيكل' },
      availableTrims: { en: 'Select Trim', ar: 'اختر الفئة' },
      driveType: { en: 'Drive Type', ar: 'نوع القيادة' },
      model: { en: 'Model', ar: 'الموديل' },
    },
    loadingScreen: {
      default: {
        en: 'Preparing the canvas for your dream car. Just a few more seconds to go!',
        ar: 'لحظات ونبدأ رحلة تصميم سيارة أحلامك... باقي ثوانٍ قليلة!',
      },
      enterConfigurator: {
        en: 'Build Your Ride',
        ar: 'ابنِ سيارتك',
      },
      loading: { en: 'Loading', ar: 'جارٍ التحميل' },
      variantLoading: { en: 'Trim is loading', ar: 'نموذج جارٍ التحميل' },
      viewLoading: {
        en: 'Applying final touches',
        ar: 'تطبيق اللمسات النهائية',
      },
      disclaimer: {
        en: 'Disclaimer',
        ar: 'تنصل',
      },
      termsConditions: {
        en: 'Terms & Conditions',
        ar: 'الشروط والأحكام',
      },
      viewSpecDetails: {
        en: 'View Spec Details',
        ar: 'ععرض تفاصيل المواصفات',
      },
      disclaimerContent: {
        en: 'Prices are subject to change without prior notice and may vary based on location, availability, or other factors.',
        ar: 'الأسعار عرضة للتغيير دون إشعار مسبق وقد تختلف بناءً على الموقع والتوافر وعوامل أخرى.',
      },
      termsModal: {
        title: {
          en: 'Configurator Use Terms and Conditions',
          ar: 'شروط وأحكام استخدام المُكوِّن',
        },
        lead: {
          en: 'By using this vehicle configurator and submitting your build, you acknowledge and agree to the following:',
          ar: 'من خلال استخدام مُكوِّن تكوين السيارة هذا وإرسال تصميمك، فإنك تقر وتوافق على ما يلي:',
        },
        sections: {
          item1: {
            title: {
              en: 'Non-Binding Submission',
              ar: 'تقديم غير ملزم',
            },
            content: {
              en: 'Submitting your vehicle configuration does not constitute a formal vehicle order or purchase agreement. No payment or deposit is collected at this stage.',
              ar: 'إرسال مواصفات سيارتك لا يُعد طلبًا رسميًا للسيارة أو اتفاقية شراء. لا يتم تحصيل أي مبلغ أو دفعة مقدمة في هذه المرحلة.',
            },
          },
          item2: {
            title: {
              en: 'Preliminary Estimate',
              ar: 'تقدير أولي',
            },
            content: {
              en: 'All pricing displayed is Manufacturer’s Suggested Retail Price (MSRP)and is provided for informational purposes only. Final pricing, taxes, fees, and applicable offers will be confirmed by the authorized dealer.',
              ar: 'جميع الأسعار المعروضة هي سعر التجزئة المقترح من الشركة المصنعة، وهي لأغراض إعلامية فقط. سيتم تأكيد الأسعار النهائية والضرائب والرسوم والعروض المطبقة من قبل الوكيل المعتمد.',
            },
          },
          item3: {
            title: {
              en: 'Dealer Follow-Up',
              ar: 'متابعة التاجر',
            },
            content: {
              en: 'Your submitted configuration will be shared with a participating dealer, who will contact you to discuss your preferences, availability, and next steps.',
              ar: 'سيتم مشاركة التكوين الذي أرسلته مع أحد التجار المشاركين، والذي سيتصل بك لمناقشة تفضيلاتك وتوافرك والخطوات التالية.',
            },
          },
          item4: {
            title: {
              en: 'Availability and Fulfillment',
              ar: 'التوفر والوفاء',
            },
            content: {
              en: 'We will make every reasonable effort to fulfill your preferences; however, final vehicle and accessories selection is subject to dealer inventory and production availability. Configurations may be limited to in-stock or incoming vehicles.',
              ar: 'سنبذل قصارى جهدنا لتلبية تفضيلاتكم، إلا أن الاختيار النهائي للمركبات والملحقات يخضع لمخزون الوكيل وتوافر الإنتاج. قد تقتصر التكوينات على المركبات المتوفرة في المخزون أو الواردة.',
            },
          },
          item5: {
            title: {
              en: 'No Obligation',
              ar: 'لا يوجد التزام',
            },
            content: {
              en: 'You are under no obligation to purchase the configured vehicle, and submitting your configuration does not create a contractual relationship between you and the manufacturer or dealer.',
              ar: 'لا يوجد أي التزام عليك بشراء السيارة المخصصة، كما أن إرسال التكوين الخاص بك لا يؤدي إلى إنشاء علاقة تعاقدية بينك وبين الشركة المصنعة أو التاجر.',
            },
          },
          item6: {
            title: {
              en: 'Use of Personal Information and Consent',
              ar: 'استخدام المعلومات الشخصية والموافقة',
            },
            content: {
              en: 'By submitting your configuration, you consent to the collection, use, and sharing of your personal information with authorized dealers to facilitate your request, in accordance with our [Privacy Statement].',
              ar: 'من خلال إرسال التكوين الخاص بك، فإنك توافق على جمع معلوماتك الشخصية واستخدامها ومشاركتها مع التجار المعتمدين لتسهيل طلبك، وفقًا لـ [بيان الخصوصية] الخاص بنا.',
            },
          },
          item7: {
            title: {
              en: 'System Limitations',
              ar: 'قيود النظام',
            },
            content: {
              en: 'While we aim to provide accurate and current information, the configurator may not reflect all available features or up-to-date pricing. Errors or omissions may occur.',
              ar: 'مع أننا نهدف إلى توفير معلومات دقيقة وحديثة، إلا أن مُكوِّن الإعدادات قد لا يعكس جميع الميزات المتاحة أو الأسعار المُحدَّثة. قد تحدث أخطاء أو سهو.',
            },
          },
        },
      },
    },
    common: {
      exit: {
        en: 'Exit',
        ar: 'خروج',
      },
      close: {
        en: 'Close',
        ar: 'إغلاق',
      },
      cancel: {
        en: 'Cancel',
        ar: 'إلغاء',
      },
      back: {
        en: 'Back',
        ar: 'خلف',
      },
      next: {
        en: 'Next',
        ar: 'التالي',
      },
      done: {
        en: 'Done',
        ar: 'تم',
      },
      exterior: {
        en: 'Exterior',
        ar: 'الخارجة',
      },
      interior: {
        en: 'Interior',
        ar: 'الداخلية',
      },
      enterFullscreen: {
        en: 'Enter Fullscreen',
        ar: 'ملء الشاشة',
      },
      exitFullscreen: {
        en: 'Exit Fullscreen',
        ar: 'إلغاء ملء الشاشة',
      },
      selectTrim: {
        en: 'Select Trim',
        ar: 'اختر الفئة',
      },
      viewConfiguration: {
        en: 'View Configuration',
        ar: 'عرض التكوين',
      },
      togglePanelTooltip: {
        en: 'Select Trim and Configuration',
        ar: 'اختر الفئة والتكوين',
      },
      nextStepTooltip: {
        en: 'Click to proceed to the next step',
        ar: 'انقر للانتقال إلى الخطوة التالية',
      },
      heading: {
        en: 'Standard Features',
        ar: 'المميزات  القياسية',
      },
      description: {
        en: 'Chevrolet Arabia',
        ar: 'شيفروليه العربية',
      },
      goBack: { en: 'Go Back', ar: 'العودة' },
      freeRoamView: { en: 'Free Roam View', ar: 'عرض التجوال الحر' },
      featureView: { en: 'Featured View', ar: 'عرض مميز' },
      configurator: { en: 'Configurator', ar: 'المُكوِّن' },
      buildYourRide: { en: 'Build Your Ride', ar: 'ابنِ سيارتك' },
      options: { en: 'Options', ar: 'الخيارات' },
      summary: { en: 'Summary', ar: 'الملخص' },
      saveYourBuild: { en: 'Save Your Build', ar: 'احفظ تكوينك' },
      startingPrice: { en: 'Starting Price', ar: 'السعر الابتدائي' },
      msrp: { en: 'MSRP', ar: 'سعر التجزئة المقترح' },
      proceed: { en: 'Proceed', ar: 'متابعة' },
    },
    tabs: {
      config: { en: 'Config', ar: 'الإعداد' },
      model: { en: 'Model', ar: 'الموديل' },
      exterior: { en: 'Exterior', ar: 'الخارجي' },
      interior: { en: 'Interior', ar: 'الداخلي' },
      options: { en: 'Options', ar: 'الخيارات' },
      summary: { en: 'Summary', ar: 'الملخص' },
    },
    cart: {
      title: {
        en: 'Your Order',
        ar: 'طلبك',
      },
      disclaimer: {
        en: 'Accessory prices and availability will be confirmed at the showroom.',
        ar: 'سيتم تأكيد أسعار الملحقات وتوافرها في صالة العرض.',
      },
      exteriorLabel: {
        en: 'Exterior:',
        ar: ':خارجي',
      },
      interiorLabel: {
        en: 'Interior:',
        ar: ':داخلي',
      },
      addedAccessories: {
        en: 'Added Accessories',
        ar: 'الإكسسوارات المضافة',
      },
      priceEstimate: {
        en: 'Price Estimate',
        ar: 'السعر التقديري',
      },
      priceBreakdown: {
        en: 'Price Breakdown',
        ar: 'تفاصيل السعر',
      },
      reserveNow: {
        en: 'Proceed',
        ar: 'متابعة',
      },
      removeAccessory: {
        en: 'Remove accessory',
        ar: 'إزالة الإكسسوار',
      },
      removeItem: { en: 'Remove', ar: 'إزالة' },
      packageCategory: { en: 'Package', ar: 'حزمة' },
    },
    saveDialog: {
      optionsTitle: {
        en: 'How would you like to proceed?',
        ar: 'كيف تريد المتابعة؟',
      },
      optionWhatsapp: { en: 'Save to WhatsApp', ar: 'حفظ على واتساب' },
      optionEmail: { en: 'Save to Email', ar: 'حفظ على البريد الإلكتروني' },
      optionTestDrive: {
        en: 'Book a Test Drive',
        ar: 'احجز اختبار قيادة',
      },
      optionCallback: {
        en: 'Request a call Back',
        ar: 'طلب معاودة الاتصال',
      },
      labelFirstName: {
        en: 'First Name*',
        ar: 'الاسم الأول',
      },
      labelLastName: {
        en: 'Last Name*',
        ar: 'اسم العائلة',
      },
      labelEmail: {
        en: 'Email*',
        ar: 'البريد الالكتروني',
      },
      labelMobileNumber: {
        en: 'Mobile Number*',
        ar: 'رقم الجوال',
      },
      labelPrefix: {
        en: 'Prefix',
        ar: 'البادئة',
      },
      labelModel: {
        en: 'Model',
        ar: 'نموذج',
      },
      labelDealership: {
        en: 'Preferred Dealership*',
        ar: 'الوكيل المفضّل',
      },
      labelComments: {
        en: 'Comments',
        ar: 'تعليقات',
      },
      labelTestDriveDate: {
        en: 'Preferred date*',
        ar: 'التاريخ المفضل*',
      },
      labelTestDriveTime: {
        en: 'Preferred time*',
        ar: 'الوقت المفضل*',
      },
      placeholderTestDriveDate: {
        en: 'Preferred date*',
        ar: 'التاريخ المفضل*',
      },
      placeholderTestDriveTime: {
        en: 'Preferred time*',
        ar: 'الوقت المفضل*',
      },
      placeholderFirstName: {
        en: 'First Name*',
        ar: 'الاسم الأول',
      },
      placeholderLastName: {
        en: 'Last Name*',
        ar: 'اسم العائلة',
      },
      placeholderEmail: {
        en: 'Email*',
        ar: 'البريد الالكتروني',
      },
      placeholderMobileNumber: {
        en: 'Mobile Number*',
        ar: 'رقم الجوال',
      },
      placeholderComments: {
        en: 'Comments',
        ar: 'تعليقات',
      },
      placeholderSelectDealership: {
        en: 'Preferred Dealership*',
        ar: 'الوكيل المفضّل',
      },
      privacyTitle: {
        en: 'Privacy statement & legal disclaimer',
        ar: 'بيان الخصوصية وإخلاء المسؤولية القانونية',
      },
      privacyText: {
        en: 'I acknowledge and understand that my information will be shared with General Motors, its affiliates, dealer network or other parties are required by law for compliance, safety campaigns, government inquiries, or similar legal process. My information may also be shared for product research and development purposes, and to manage customer relationships to provide support. GM’s Privacy Statement addresses how GM handles the personal information shared with us.',
        ar: 'أقر وأتفهم بأنة سيتم مشاركة معلوماتي مع شركة جنرال موتورز، والشركات التابعة لها، وشبكة الوكلاء أو الأطراف الأخرى بموجب القانون للامتثال وحملات السلامة واستفسارات الحكومية وأي إجراء قانوني مماثل. وقد تتم مشاركة معلوماتي أيضًا لأغراض البحث وتطوير المنتجات ، وإدارة علاقة العملاء لتوفير الدعم اللازم. يوضح بيان خصوصية جنرال موتورز على موقعنا كيفية تعامل جنرال موتورز مع المعلومات الشخصية التي تمت مشاركتها معنا.',
      },
      privacyReadMore: {
        en: 'Read more',
        ar: 'اقرأ المزيد',
      },
      privacyMarketingConsent: {
        en: 'I wish to receive any marketing information or have my information shared with third parties for purposes of providing me with marketing information.',
        ar: 'أرغب في تلقي معلومات تسويقية أو مشاركة معلوماتي مع جهات خارجية لغاية تزويدي بمعلومات تسويقية',
      },
      buttonSend: {
        en: 'Send Configuration',
        ar: 'إرسال التكوين',
      },
      buttonSending: {
        en: 'Submitting...',
        ar: 'تقديم...',
      },
      buttonSent: {
        en: 'Sent!',
        ar: 'مرسل!',
      },
      buttonRetry: {
        en: 'Retry',
        ar: 'إعادة المحاولة',
      },
      buttonClose: {
        en: 'Okay, Got it',
        ar: 'حسنًا، فهمت',
      },
      successTitle: {
        en: 'Saved',
        ar: 'محفوظ',
      },
      successLabelModel: {
        en: 'Model Name',
        ar: 'اسم النموذج',
      },
      successLabelVariant: {
        en: 'Variant Name',
        ar: 'اسم البديل',
      },
      successLabelName: {
        en: 'Name',
        ar: 'اسم',
      },
      successLabelEmail: {
        en: 'Email*',
        ar: 'البريد الالكتروني',
      },
      successLabelMobile: {
        en: 'Mobile Number*',
        ar: 'رقم الجوال',
      },
      qrTitle: {
        en: 'Your Unique Configuration QR',
        ar: 'رمز الاستجابة السريعة لتكوينك',
      },
      qrDescription: {
        en: 'Show this QR code to your nearest dealer to access your saved configuration.',
        ar: 'أظهر رمز الاستجابة السريعة هذا لتاجر التجزئة الأقرب للوصول إلى التكوين المحفوظ.',
      },
      qrNoteLabel: {
        en: 'Note:',
        ar: 'ملاحظة:',
      },
      qrNote: {
        en: 'Only authorized dealers can view this configuration.',
        ar: 'يمكن للوكلاء المعتمدين فقط عرض هذا التكوين.',
      },
      qrDownload: {
        en: 'Download QR',
        ar: 'تحميل QR',
      },
      qrSave: {
        en: 'Save QR',
        ar: 'حفظ QR',
      },
      errorTitle: {
        en: 'Submission Failed',
        ar: 'فشل التقديم',
      },
      errorMessage: {
        en: 'We encountered an error, please try again.',
        ar: 'واجهنا خطأ، يرجى المحاولة مرة أخرى.',
      },
    },
    featureGroups: {
      exteriorDesign: {
        en: 'Exterior Design & Styling',
        ar: 'التصميم الخارجي والتصميم',
      },
      interiorComfort: {
        en: 'Interior Comfort & Features',
        ar: 'الراحة والميزات الداخلية',
      },
      technologyConvenience: {
        en: 'Technology & Convenience',
        ar: 'التكنولوجيا والراحة',
      },
      safetyAssistance: {
        en: 'Safety & Driver Assistance',
        ar: 'السلامة ومساعدة السائق',
      },
    },
    sidePanel: {
      menu: { en: 'Main Menu', ar: 'القائمة الرئيسية' },
      search: { en: 'Search', ar: 'بحث' },
      settings: { en: 'Settings', ar: 'الإعدادات' },
      profile: { en: 'Profile', ar: 'الملف الشخصي' },
      notifications: { en: 'Notifications', ar: 'الإشعارات' },
      help: { en: 'Help & Support', ar: 'المساعدة والدعم' },
      filter: { en: 'Filter Options', ar: 'خيارات التصفية' },
      favorites: { en: 'Favorites', ar: 'المفضلة' },
      history: { en: 'History', ar: 'التاريخ' },
      tools: { en: 'Tools', ar: 'الأدوات' },
      variants: { en: 'Variants', ar: 'الأنواع' },
      exteriorColor: { en: 'Exterior Color', ar: 'لون الهيكل' },
      interiorColor: { en: 'Interior Color', ar: 'ألوان المقصورة' },
      features: { en: 'Features', ar: 'الميزات' },
      step: { en: 'Step', ar: 'خطوة' },
      openPanel: { en: 'Open Panel', ar: 'فتح اللوحة' },
      closePanel: { en: 'Close Panel', ar: 'إغلاق اللوحة' },
    },
    landscape: {
      message: {
        en: 'Please rotate your device to portrait mode',
        ar: 'يرجى تدوير جهازك إلى الوضع الرأسي',
      },
      subtitle: {
        en: 'For the best experience, please turn your device upright.',
        ar: 'للحصول على أفضل تجربة، يرجى إمساك جهازك بشكل رأسي.',
      },
    },
    cameraViewToggle: {
      ext: { en: 'Ext', ar: 'خارجي' },
      int: { en: 'Int', ar: 'داخلي' },
      dualTone: { en: 'Dual-tone', ar: 'لون مزدوج' },
    },
    bottomDrawer: {
      variants: { en: 'Variants', ar: 'الأنواع' },
      interactions: { en: 'Interactions', ar: 'التفاعلات' },
      accessories: { en: 'Options & Accessories', ar: 'الإكسسوارات' },
      features: { en: 'Features', ar: 'الميزات' },
      highlights: { en: 'Highlights', ar: 'الميزات البارزة' },
      moreOptions: { en: 'More Options', ar: 'المزيد من الخيارات' },
      colorSelector: { en: 'Color Selector', ar: 'محدد اللون' },
      exteriorColor: { en: 'Exterior Color', ar: 'لون الهيكل' },
      interiorColor: { en: 'Interior Color', ar: 'لون المقصورة' },
      wheels: { en: 'Wheels', ar: 'العجلات' },
      roofs: { en: 'Roofs', ar: 'الأسقف' },
      performance: { en: 'Performance', ar: 'الأداء' },
      packages: { en: 'Packages', ar: 'الحزم' },
      chargingOptions: { en: 'Charging Options', ar: 'خيارات الشحن' },
      safetyServices: { en: 'Safety & Services', ar: 'السلامة والخدمات' },
      expand: { en: 'Expand', ar: 'توسيع' },
      minimize: { en: 'Minimize', ar: 'تصغير' },
      freeRoamView: { en: 'Free Roam View', ar: 'عرض التجوال الحر' },
      featureView: { en: 'Featured View', ar: 'العرض المميز' },
    },
    qr: {
      scan: { en: 'Scan QR', ar: 'امسح رمز الاستجابة السريعة' },
      scanWithPhone: {
        en: 'Scan with your phone to view in your Space',
        ar: 'امسح باستخدام هاتفك لعرضه في مساحتك',
      },
    },
    share: {
      title: { en: 'Share Configuration', ar: 'مشاركة التكوين' },
      description: {
        en: 'Share your vehicle configuration with others',
        ar: 'شارك تكوين سيارتك مع الآخرين',
      },
      twitterTitle: {
        en: 'Check out my Chevrolet Groove configuration!',
        ar: 'شاهد تكوين سيارتي Chevrolet Groove!',
      },
      whatsappTitle: {
        en: 'Check out my Chevrolet Groove configuration!',
        ar: 'شاهد تكوين سيارتي Chevrolet Groove!',
      },
      linkedinTitle: {
        en: 'My Chevrolet Groove Configuration',
        ar: 'تكويني لسيارة Chevrolet Groove',
      },
      linkedinSummary: {
        en: 'Check out my custom Chevrolet Groove configuration!',
        ar: 'شاهد تكويني المخصص لسيارة Chevrolet Groove!',
      },
      emailSubject: {
        en: 'Check out my Chevrolet Groove Configuration',
        ar: 'شاهد تكوين سيارتي Chevrolet Groove',
      },
      emailBody: {
        en: 'I configured my dream Chevrolet Groove. Take a look:',
        ar: 'قمت بتكوين سيارة Chevrolet Groove المثالية. ألق نظرة:',
      },
      linkLabel: { en: 'Or copy link:', ar: 'أو انسخ الرابط:' },
      copied: {
        en: 'Copied',
        ar: 'تم نسخ الرابط إلى الحافظة!',
      },
      copy: { en: 'Copy', ar: 'نسخ' },
      saveAndShare: { en: 'Save & Share', ar: 'حفظ ومشاركة' },
      copyLink: { en: 'Copy link', ar: 'نسخ الرابط' },
      facebook: { en: 'Facebook', ar: 'Facebook' },
      twitter: { en: 'Twitter', ar: 'Twitter' },
      gmail: { en: 'Gmail', ar: 'Gmail' },
      whatsapp: { en: 'Whatsapp', ar: 'Whatsapp' },
    },
    topBar: {
      matchingVehiclesFound: {
        en: 'Matching Vehicles Found',
        ar: 'تم العثور على المركبات المطابقة',
      },
    },
    bottomBar: {
      priceLabel: { en: 'ex showroom price', ar: 'سعر صالة العرض' },
      leads: { en: 'Save', ar: 'حفظ' },
      findLocalMatches: {
        en: 'Find Local Matches',
        ar: 'اعثر على التطابقات المحلية',
      },
      save: { en: 'Save', ar: 'حفظ' },
      item: { en: 'Item', ar: 'العنصر' },
      items: { en: 'Items', ar: 'عناصر' },
      totalToPay: { en: 'Total to pay', ar: 'الإجمالي للدفع' },
      bookTestDrive: { en: 'Save', ar: 'حفظ' },
      requestQuote: { en: 'Request a Quote', ar: 'طلب عرض سعر' },
      downloadBrochure: { en: 'Download Brochure', ar: 'تحميل الكتيب' },
    },
    priceSummary: {
      title: { en: 'Price Summary', ar: 'ملخص السعر' },
      enterLocation: { en: 'Enter Location', ar: 'أدخل الموقع' },
      selectCountry: { en: 'Location', ar: 'الموقع' },
      selectCity: { en: 'Location', ar: 'الموقع' },
      note: {
        en: 'Note : Prices might vary by state and all prices listed are on road price, T&C apply.',
        ar: 'ملاحظة: قد تختلف الأسعار حسب الولاية وجميع الأسعار المذكورة هي الأسعار على الطريق، تطبق الشروط والأحكام.',
      },
      breakdown: { en: 'Price Breakdown', ar: 'تفصيل السعر' },
      color: { en: 'Color', ar: 'اللون' },
      variant: { en: 'Variant', ar: 'الفئة' },
      included: { en: 'Included', ar: 'مشمول' },
      total: { en: 'Total', ar: 'الإجمالي' },
    },
    featureDialog: {
      playPause: { en: 'Play-Pause', ar: 'تشغيل-إيقاف' },
    },
    selectDealership: {
      title: { en: 'Select a Dealer', ar: 'اختر وكيلاً' },
      subtitle: {
        en: 'Please select a dealer to find local matches similar to your configuration',
        ar: 'يرجى اختيار وكيل للعثور على سيارات مطابقة لتكوينك في منطقتك',
      },
      city: { en: 'UAE', ar: 'الإمارات' },
      dealersIn: { en: 'Dealers in', ar: 'وكلاء في' },
      dealersInCity: { en: 'Dealers in {{city}}', ar: 'وكلاء في {{city}}' },
      availableInLocations: {
        en: 'Available in {{count}} locations of {{city}}',
        ar: 'متوفرة في {{count}} مواقع في {{city}}',
      },
      proceed: {
        en: 'Select Dealer and Proceed to Configurator',
        ar: 'اختر الوكيل وتابع إلى المتكون',
      },
    },
    countryNames: {
      UAE: { en: 'UAE', ar: 'الإمارات' },
      KSA: { en: 'KSA', ar: 'السعودية' },
      KW: { en: 'Kuwait', ar: 'الكويت' },
      default: { en: 'UAE', ar: 'الإمارات' },
    },
    localMatches: {
      exterior: { en: 'Exterior', ar: 'خارجي' },
      interior: { en: 'Interior', ar: 'داخلي' },
      dualTone: { en: 'Dual-tone', ar: 'لون مزدوج' },
      yourConfiguration: { en: 'Current Configuration', ar: 'التكوين الحالي' },
      viewConfiguration: {
        en: 'View your configuration details',
        ar: 'عرض تفاصيل تكوينك',
      },
      yourBuildDetails: { en: 'Your Build Details', ar: 'تفاصيل بنائك' },
      options: { en: 'Options', ar: 'الخيارات' },
      save: { en: 'Save', ar: 'حفظ' },
      dealersIn: { en: 'Dealers in', ar: 'وكلاء في' },
      changeDealer: { en: 'Change Dealer', ar: 'تغيير الوكيل' },
      carsAvailable: {
        en: 'Cars Available at the Selected Dealership',
        ar: 'السيارات المتوفرة لدى الوكيل المختار',
      },
      matchesFound: { en: 'Matches found', ar: 'تطابق تم العثور عليه' },
      startingFrom: { en: 'Starting from', ar: 'يبدأ من' },
      getQuote: { en: 'Proceed', ar: 'احصل على عرض سعر' },
      viewDifferences: { en: 'View Differences', ar: 'عرض الاختلافات' },
      match: { en: 'Match', ar: 'تطابق' },
      closestMatchFound: {
        en: 'Closest Match Found',
        ar: 'أقرب تطابق تم العثور عليه',
      },
      buildDetails: { en: 'Build Details', ar: 'تفاصيل التكوين' },
      trim: { en: 'Trim', ar: 'الفئة' },
      matchScoreTooltip: {
        en: 'This score indicates how closely this vehicle matches your configuration.',
        ar: 'يشير هذا النتيجة إلى مدى تطابق هذه السيارة مع تكوينك.',
      },
      exactMatch: { en: 'Exact Match', ar: 'تطابق تام' },
      similarMatch: { en: 'Close Match', ar: 'تطابق قريب' },
      availableNow: { en: 'Available Now', ar: 'متوفر الآن' },
      whatYouConfigured: { en: 'What you configured', ar: 'ما قمت بتكوينه' },
      matchingInStock: { en: 'Matching vehicle available in stock', ar: 'مركبة مطابقة متوفرة في المخزون' },
      noMatches: {
        en: 'No matching vehicles found in your area.',
        ar: 'لم يتم العثور على سيارات مطابقة في منطقتك.',
      },
      closeMatchDetails: { en: 'Close Match Details', ar: 'تفاصيل التطابق القريب' },
      matchesYourSelection: { en: 'Matches your selection', ar: 'يطابق اختيارك' },
      sameAsWhatYouSelected: { en: 'Same as what you selected', ar: 'نفس ما اخترته' },
      replacesYourSelection: { en: 'Replaces your selection', ar: 'يستبدل اختيارك' },
      selectionNotIncluded: { en: 'Selection not included', ar: 'الاختيار غير مشمول' },
      includedInThisVehicle: { en: 'Included in this vehicle', ar: 'مشمول في هذه المركبة' },
      additionalFeatureAlreadyIncluded: { en: 'Additional feature already included', ar: 'ميزة إضافية مشمولة بالفعل' },
      stockDisclaimer: { en: 'Available stock may include extra features not selected in your original configuration.', ar: 'قد تشمل المركبات المتاحة ميزات إضافية لم تختارها في تكوينك الأصلي.' },
      additionalInstalledOptionsLegend: { en: 'Included in this vehicle', ar: 'مشمول في هذه المركبة' },
      vehicleDetails: { en: 'Vehicle Details', ar: 'تفاصيل المركبة' },
      installedOptionsTitle: { en: 'Comes with vehicle', ar: 'يأتي مع المركبة' },
      additionalInstalledOptionsSubtitle: { en: 'Additional Installed Options:', ar: 'الخيارات المركبة الإضافية:' },
      specTrim: { en: 'Trim', ar: 'الفئة' },
      specExteriorColor: { en: 'Exterior Color', ar: 'اللون الخارجي' },
      specInteriorColor: { en: 'Interior Color', ar: 'اللون الداخلي' },
      specWheels: { en: 'Wheels', ar: 'العجلات' },
      specPackage: { en: 'Package', ar: 'الحزمة' },
      title: {
        en: 'Configurations Found',
        ar: 'تكوينات تم العثور عليها',
      },
      message: {
        en: 'We Found a few local matches similar to your current configuration. Click to view Matches',
        ar: 'وجدنا بعض التطابقات المحلية المشابهة لتكوينك الحالي. انقر لعرض التطابقات',
      },
    },
    trim: {
      selection: { en: 'Trim Selection', ar: 'اختيار الفئة' },
    },
    paymentPage: {
      exteriorColor: { en: 'Exterior Color', ar: 'لون الهيكل' },
      interiorColor: { en: 'Interior Color', ar: 'لون المقصورة' },
      accessory: { en: 'Accessory', ar: 'ملحق' },
      closePayment: { en: 'Close', ar: 'إغلاق' },
    },
    packageModal: {
      includes: { en: 'Includes:', ar: 'يشمل:' },
      of: { en: 'of', ar: 'من' },
      totalPriceChange: {
        en: 'Total Price Change:',
        ar: 'إجمالي تغيير السعر:',
      },
      item: { en: 'Item', ar: 'غرض' },
      items: { en: 'Items', ar: 'أغراض' },
      addPackage: { en: 'Add Package', ar: 'إضافة الحزمة' },
      noThanks: { en: 'No Thanks', ar: 'لا شكراً' },
      alreadyApplied: { en: 'Already Applied', ar: 'مُطبّق بالفعل' },
    },
    changesRequired: { en: 'Changes Required', ar: 'التغييرات المطلوبة' },
    changesRequiredDescription: {
      en: 'The package you selected conflicts with your current selections. Please review the changes below.',
      ar: 'الحزمة التي اخترتها تتعارض مع اختياراتك الحالية. يرجى مراجعة التغييرات أدناه.',
    },
    itemsThatWillBeRemoved: {
      en: 'Items that will be removed',
      ar: 'العناصر التي سيتم إزالتها',
    },
    itemsThatWillBeAdded: {
      en: 'Items that will be added',
      ar: 'العناصر التي سيتم إضافتها',
    },
    applyChanges: { en: 'Apply Changes', ar: 'تطبيق التغييرات' },
    previewFeature: { en: 'Preview Feature', ar: 'معاينة الميزة' },
    exploreFeatures: { en: 'Explore Features', ar: 'استكشف الميزات' },
    buildAndPrice: { en: 'Build & Price', ar: 'البناء والسعر' },
    features: {
      preview: { en: 'Preview Feature', ar: 'معاينة المميزات' },
      playAll: { en: 'PLAY ALL', ar: 'تشغيل الكل' },
      title: { en: 'Features', ar: 'المميزات' },
    },
    header: {
      localMatches: { en: 'Local\nMatches', ar: 'التطابقات\nالمحلية' },
      matchingVehicles: { en: 'Matching vehicles', ar: 'مركبات مطابقة' },
      configurationsFound: { en: 'Configurations Found', ar: 'تكوينات تم العثور عليها' },
    },
    configPanel: {
      playAll: { en: 'Play All', ar: 'تشغيل الكل' },
      pause: { en: 'Pause', ar: 'إيقاف مؤقت' },
      exteriorColors: { en: 'Exterior Colors', ar: 'ألوان الهيكل الخارجي' },
      interiorColors: { en: 'Interior Colors', ar: 'ألوان الداخلية' },
      roofOptions: { en: 'Roof Options', ar: 'خيارات السقف' },
      interiorOptions: { en: 'Interior Options', ar: 'خيارات الداخلية' },
      exteriorAccessories: { en: 'Exterior Accessories', ar: 'إكسسوارات خارجية' },
      noAdditionalOptions: { en: 'No additional options available for the selected trim.', ar: 'لا توجد خيارات إضافية متاحة للفئة المختارة.' },
      noFilters: { en: 'No filters available.', ar: 'لا توجد فلاتر متاحة.' },
      noVariantsMatch: { en: 'No trims match the selected filters.', ar: 'لا توجد فئات تطابق الفلاتر المختارة.' },
      configurationBreakdown: { en: 'Configuration Breakdown', ar: 'تفاصيل التكوين' },
      yourConfiguration: { en: 'Your Configuration', ar: 'تكوينك' },
      trim: { en: 'Trim', ar: 'الفئة' },
      total: { en: 'Total', ar: 'الإجمالي' },
    },
    conflictDialog: {
      description: { en: 'In order to apply this, the following item(s) will need to be removed due to compatibility constraints. Please review the item(s) below and proceed with your change.', ar: 'لتطبيق هذا، يجب إزالة العناصر التالية بسبب قيود التوافق. يرجى مراجعة العناصر أدناه والمتابعة بتغييرك.' },
    },
    viewport: {
      changeBackground: { en: 'Change Background', ar: 'تغيير الخلفية' },
      selectLanguage: { en: 'Switch Language (عربي)', ar: 'تبديل اللغة (EN)' },
      menu: { en: 'Menu', ar: 'القائمة' },
      exploreFeatures: { en: 'Explore Features', ar: 'استكشف الميزات' },
    },
  },
  dealers: {
    UAE: [
      {
        id: 0,
        country: 'UAE',
        value: 'UAE',
        dealerId: '122255',
        dealerName: 'Al Ghandi Auto',
        label: {
          en: 'Al Ghandi Auto - Al Quoz 3 Industrial Area Sheikh Zayed Road, Dubai',
          ar: 'الكندي للسيارات - القوز 3، المنطقة الصناعية، طريق الشيخ زايد، دبي',
        },
      },
      {
        id: 1,
        country: 'UAE',
        value: 'UAE',
        dealerId: '256427',
        dealerName: 'Al Ghandi Auto',
        label: {
          en: 'Al Ghandi Auto - Double Cola Road, Sharjah',
          ar: 'الكندي للسيارات - طريق دبل كولا، الشارقة',
        },
      },
      {
        id: 2,
        country: 'UAE',
        value: 'UAE',
        dealerId: '256428',
        dealerName: 'Al Ghandi Auto',
        label: {
          en: 'Al Ghandi Auto - Madhab Mohammad Bin Mattar Street, Fujairah',
          ar: 'الغاندي للسيارات - شارع مذهب محمد بن مطر، الفجيرة',
        },
      },
      {
        id: 3,
        country: 'UAE',
        value: 'UAE',
        dealerId: '256429',
        dealerName: 'Al Ghandi Auto',
        label: {
          en: 'Al Ghandi Auto - Al Nadiyah Sheikh Mohammed Bin Salem Road, Ras Al Khaimah',
          ar: 'الكندي للسيارات - الندية طريق الشيخ محمد بن سالم، رأس الخيمة',
        },
      },
      {
        id: 4,
        country: 'UAE',
        value: 'UAE',
        dealerId: '301597',
        dealerName: 'Al Ghandi Auto',
        label: {
          en: 'Al Ghandi Auto - Al Itihad Road - showroom , Al Khabaisi, Deira, Dubai',
          ar: 'الكندي للسيارات - طريق الاتحاد - صالة عرض , الخبيصي, ديرة, دبي',
        },
      },
      {
        id: 8,
        country: 'UAE',
        value: '',
        dealerId: '334821',
        dealerName: 'Al Ghandi Auto',
        label: {
          en: 'Al Ghandi - Mirdiff City Center',
          ar: 'الغاندي - مردف سيتي سنتر',
        },
      },
      {
        id: 5,
        country: 'UAE',
        value: 'UAE',
        dealerId: '323482',
        dealerName: 'Bin Hamoodah Auto',
        label: {
          en: 'Bin Hamoodah Auto - Madinat Zayed, Al Dhafra',
          ar: 'بن حمودة للسيارات - مدينة زايد، الظفرة',
        },
      },
      {
        id: 6,
        country: 'UAE',
        value: 'UAE',
        dealerId: '122552',
        dealerName: 'Bin Hamoodah Auto',
        label: {
          en: 'Bin Hamoodah Auto - Al Ain',
          ar: 'بن حمودة للسيارات - العين',
        },
      },
      {
        id: 7,
        country: 'UAE',
        value: 'UAE',
        dealerId: '122233',
        dealerName: 'Bin Hamoodah Auto',
        label: {
          en: 'Bin Hamoodah Auto - Abu Dhabi Main',
          ar: 'بن حمودة للسيارات - أبوظبي الرئيسية',
        },
      },
    ],
    KSA: [
      {
        id: 0,
        country: 'KSA',
        value: 'KSA',
        dealerId: '307261',
        dealerName: 'Aljomaih Auto',
        label: {
          en: 'Aljomaih Auto - Al Aziziyah Madina Road, Jeddah',
          ar: 'الجميح للسيارات - العزيزية طريق المدينة، جدة',
        },
      },
      {
        id: 1,
        country: 'KSA',
        value: 'KSA',
        dealerId: '164034',
        dealerName: 'Aljomaih Auto',
        label: {
          en: 'Aljomaih Auto - Hail City King Abdullah Road. Hail',
          ar: 'الجميح للسيارات - مدينة حائل طريق الملك عبدالله. يشيد',
        },
      },
      {
        id: 2,
        country: 'KSA',
        value: 'KSA',
        dealerId: '164035',
        dealerName: 'Aljomaih Auto',
        label: {
          en: 'Aljomaih Auto - Alshlhoub King Abdullah Road, Al Jouf',
          ar: 'الجميح للسيارات - الشلهوب طريق الملك عبدالله، الجوف',
        },
      },
      {
        id: 3,
        country: 'KSA',
        value: 'KSA',
        dealerId: '307271',
        dealerName: 'Aljomaih Auto',
        label: {
          en: 'Aljomaih Auto - Qassim King Abdulaziz Road, Buraidah',
          ar: 'الجميح للسيارات - القصيم طريق الملك عبد العزيز، بريدة',
        },
      },
      {
        id: 4,
        country: 'KSA',
        value: 'KSA',
        dealerId: '260921',
        dealerName: 'Aljomaih Auto',
        label: {
          en: 'Aljomaih Auto - Al Jededah Road, Arar',
          ar: 'الجميح للسيارات - طريق الجديدة، عرعر',
        },
      },
    ],
    KW: [
      {
        id: 0,
        country: 'KW',
        value: 'KW',
        dealerId: '122620',
        dealerName: 'Alamana Auto',
        label: {
          en: 'Alamana Auto - Al Rai 4th Ring Road, Al Rai',
          ar: 'الامانة للسيارات - الري الطريق الدائري الرابع، الري',
        },
      },
      {
        id: 1,
        country: 'KW',
        value: 'KW',
        dealerId: '174979',
        dealerName: 'Alamana Auto',
        label: {
          en: 'Alamana Auto - Fahaheel Industrial Area Dabbous Street, Fahaheel',
          ar: 'الامانة للسيارات - الفحيحيل الصناعية شارع الدبوس، الفحيحيل',
        },
      },
    ],
    BHD: [
      {
        id: 0,
        country: 'BHD',
        value: 'BHD',
        dealerId: '122215',
        dealerName: 'National Motor Company W.L.L',
        label: {
          en: 'National Motor Company W.L.L, Al Qarya 475 Shk. Jaber Al Subah Hwy',
          ar: 'الشركة الوطنية للسيارات ذ.م.م، القرية 475 شارع الشيخ. طريق جابر الصباح',
        },
      },
    ],
    IQD: [
      {
        id: 0,
        country: 'IQD',
        value: 'IQD',
        dealerId: '295876',
        dealerName: 'MAC International FZE',
        label: {
          en: 'MAC International FZE, Al-Karada Sadoun Street',
          ar: 'شركة ماك الدولية (منطقة حرة)، شارع الكرادة سعدون',
        },
      },
      {
        id: 1,
        country: 'IQD',
        value: 'IQD',
        dealerId: '295879',
        dealerName: 'MAC International FZE',
        label: {
          en: 'MAC International FZE, Kurdistan District 100 m Street',
          ar: 'ماك إنترناشونال إف زي إي، منطقة كردستان، شارع ١٠٠ متر',
        },
      },
    ],
    LBP: [
      {
        id: 0,
        country: 'LBP',
        value: 'LBP',
        dealerId: '122226',
        dealerName: 'IMPEX',
        label: {
          en: 'IMPEX -  Badaro, Near the National Museum , Beirut',
          ar: 'إمبكس - بدارو، بالقرب من المتحف الوطني، بيروت',
        },
      },
    ],
    QAR: [
      {
        id: 0,
        country: 'QAR',
        value: 'QAR',
        dealerId: '274766',
        dealerName: 'Jaidah',
        label: {
          en: 'Jaidah Square Showroom - Al Matar Street Umm Ghuwallina, Doha',
          ar: 'صالة عرض "جايدة سكوير" - شارع المطار، أم غويلينة، الدوحة',
        },
      },
    ],
    OMR: [
      {
        id: 0,
        country: 'OMR',
        value: 'OMR',
        dealerId: '122219',
        dealerName: 'Oman Trading Establisment',
        label: {
          en: 'Oman Trading Establisment LLC, Ghalla Al Maardh Street',
          ar: 'مؤسسة عمان التجارية ش.م.م، شارع غلا المعرض',
        },
      },
      {
        id: 1,
        country: 'OMR',
        value: 'OMR',
        dealerId: '234217',
        dealerName: 'Oman Trading Establisment',
        label: {
          en: 'Oman Trading Establisment LLC, Al Farq Street',
          ar: 'مؤسسة عمان التجارية ذ.م.م، شارع الفرق',
        },
      },
      {
        id: 2,
        country: 'OMR',
        value: 'OMR',
        dealerId: '260896',
        dealerName: 'Oman Trading Establisment',
        label: {
          en: 'Oman Trading Establisment LLC, Hail Al Shumaliah Street',
          ar: 'مؤسسة عمان التجارية ذ.م.م، شارع حيل الشمالية',
        },
      },
    ],
    JOD: [
      {
        id: 0,
        country: 'JOD',
        value: 'JOD',
        dealerId: '189129',
        dealerName: 'Leading Vehicles',
        label: {
          en: 'The Leading Vehicles Co. Ltd, Al Rawabi',
          ar: 'شركة المركبات الرائدة المحدودة، الروابي',
        },
      },
    ],
    default: [
      {
        id: 0,
        country: 'UAE',
        value: 'UAE',
        dealerId: '122255',
        dealerName: 'Al Ghandi Auto',
        label: {
          en: 'Al Ghandi Auto - Al Quoz 3 Industrial Area Sheikh Zayed Road, Dubai',
          ar: 'الكندي للسيارات - القوز 3، المنطقة الصناعية، طريق الشيخ زايد، دبي',
        },
      },
      {
        id: 1,
        country: 'UAE',
        value: 'UAE',
        dealerId: '256427',
        dealerName: 'Al Ghandi Auto',
        label: {
          en: 'Al Ghandi Auto - Double Cola Road, Sharjah',
          ar: 'الكندي للسيارات - طريق دبل كولا، الشارقة',
        },
      },
      {
        id: 2,
        country: 'UAE',
        value: 'UAE',
        dealerId: '256428',
        dealerName: 'Al Ghandi Auto',
        label: {
          en: 'Al Ghandi Auto - Madhab Mohammad Bin Mattar Street, Fujairah',
          ar: 'الغاندي للسيارات - شارع مذهب محمد بن مطر، الفجيرة',
        },
      },
      {
        id: 3,
        country: 'UAE',
        value: 'UAE',
        dealerId: '256429',
        dealerName: 'Al Ghandi Auto',
        label: {
          en: 'Al Ghandi Auto - Al Nadiyah Sheikh Mohammed Bin Salem Road, Ras Al Khaimah',
          ar: 'الكندي للسيارات - الندية طريق الشيخ محمد بن سالم، رأس الخيمة',
        },
      },
      {
        id: 4,
        country: 'UAE',
        value: 'UAE',
        dealerId: '301597',
        dealerName: 'Al Ghandi Auto',
        label: {
          en: 'Al Ghandi Auto - Al Itihad Road - showroom , Al Khabaisi, Deira, Dubai',
          ar: 'الكندي للسيارات - طريق الاتحاد - صالة عرض , الخبيصي, ديرة, دبي',
        },
      },
      {
        id: 8,
        country: 'UAE',
        value: '',
        dealerId: '334821',
        dealerName: 'Al Ghandi Auto',
        label: {
          en: 'Al Ghandi - Mirdiff City Center',
          ar: 'الغاندي - مردف سيتي سنتر',
        },
      },
      {
        id: 5,
        country: 'UAE',
        value: 'UAE',
        dealerId: '323482',
        dealerName: 'Bin Hamoodah Auto',
        label: {
          en: 'Bin Hamoodah Auto - Madinat Zayed, Al Dhafra',
          ar: 'بن حمودة للسيارات - مدينة زايد، الظفرة',
        },
      },
      {
        id: 6,
        country: 'UAE',
        value: 'UAE',
        dealerId: '122552',
        dealerName: 'Bin Hamoodah Auto',
        label: {
          en: 'Bin Hamoodah Auto - Al Ain',
          ar: 'بن حمودة للسيارات - العين',
        },
      },
      {
        id: 7,
        country: 'UAE',
        value: 'UAE',
        dealerId: '122233',
        dealerName: 'Bin Hamoodah Auto',
        label: {
          en: 'Bin Hamoodah Auto - Abu Dhabi Main',
          ar: 'بن حمودة للسيارات - أبوظبي الرئيسية',
        },
      },
    ],
  },
  featureFlags: {
    showPriceInPresentation: false,
    showLogo: true,
    showSpecDetailsButton: true,
    useChevroletFont: true,
    showLocalMatches: true,
    showDisclaimerInPresentation: false,
    // Per-item pricing (card / MODEL / SUMMARY price lines, colours in the total)
    // is opt-in; this project keeps the header-only pricing it has always had.
    showPrices: true, // per-item prices on cards + summary breakdown, like Corvette
    showDisplayCarPrice: true,
    showCart: false
  },
  config: {
    // Display currency + USD conversion factor per region, keyed by the
    // `country` URL parameter (local = usd * fx). Prices were re-derived
    // from the AED figure: usd = AED / 3.68, then usd * fx per country.
    regions: {
      KSA: { label: 'KSA', currency: 'SAR', fx: 3.75 },
      UAE: { label: 'UAE', currency: 'AED', fx: 3.68 },
      OMR: { label: 'Oman', currency: 'OMR', fx: 0.39 },
      BHD: { label: 'Bahrain', currency: 'BHD', fx: 0.38 },
      QAR: { label: 'Qatar', currency: 'QAR', fx: 3.64 },
      JOD: { label: 'Jordan', currency: 'JOD', fx: 0.71 },
      KW: { label: 'Kuwait', currency: 'KWD', fx: 0.31 },
      LBP: { label: 'Lebanon', currency: 'USD', fx: 1 }, // GM quotes Lebanon in USD
      IQD: { label: 'Iraq', currency: 'USD', fx: 1 }, // priced in USD, like Lebanon
      default: { label: 'UAE', currency: 'AED', fx: 3.68 },
    },
    // Lead-side model name — 1.0's `config.model`, which is a lead label
    // distinct from 2.0's `config.model` (the models[] key).
    leadModel: 'groove',
    // Dial code (prefixed onto mobile_number) and Salesforce lead country
    // code (metadome_country_code), keyed by the `country` URL parameter.
    countryCodes: {
      UAE: {
        code: '+971',
        leadCode: 'AE',
      },
      KSA: {
        code: '+966',
        leadCode: 'SA',
      },
      KW: {
        code: '+965',
        leadCode: 'KW',
      },
      BHD: {
        code: '+973',
        leadCode: 'BH',
      },
      IQD: {
        code: '+964',
        leadCode: 'IQ',
      },
      LBP: {
        code: '+961',
        leadCode: 'LB',
      },
      OMR: {
        code: '+968',
        leadCode: 'OM',
      },
      QAR: {
        code: '+974',
        leadCode: 'QA',
      },
      JOD: {
        code: '+962',
        leadCode: 'JO',
      },
      default: {
        code: '+971',
        leadCode: 'AE',
      },
    },
    model: 'Groove',
    modelName: 'Groove',
    pageTitle: 'Groove Configurator',
    client: 'GM',
    brand: 'chevrolet',
    modelId: 'CHTAHMCM_2025',
    configurationId: 'l25zrwex1u93',
    externalAnalytics: {
      brand: 'chevrolet',
      provider: 'adobe',
      modelName: 'groove'
    },
    presentation: {
      video: 'videos/2386/presentation_video_1.webm',
      loadingImage: 'images/2386/sliderImage.webp',
      transitionImage: 'images/2386/transition_logo.png',
      headerLogoKeepColor: true,
      keyHighlights: [
        {
          index: 0,
          show: true,
          title: {
            en: '',
            ar: ''
          },
          value: {
            en: '145 HP',
            ar: '145 حصان'
          },
          unit: ''
        },
        {
          index: 1,
          show: true,
          title: {
            en: 'Torque',
            ar: 'عزم الدوران'
          },
          value: {
            en: '255 NM',
            ar: '255 نيوتن متر'
          },
          unit: ''
        },
        {
          index: 2,
          show: true,
          title: {
            en: 'Engine',
            ar: 'المحرك'
          },
          value: {
            en: '1.5L Turbo',
            ar: 'محرك توربو سعة 1.5 لتر'
          },
          unit: ''
        }
      ]
    }
  },
  saveFormConfig: {
    fields: [
      {
        id: 'firstName',
        type: 'text',
        required: true,
        labelKey: 'saveDialog.labelFirstName',
        placeholderKey: 'saveDialog.placeholderFirstName',
      },
      {
        id: 'lastName',
        type: 'text',
        required: true,
        labelKey: 'saveDialog.labelLastName',
        placeholderKey: 'saveDialog.placeholderLastName',
      },
      {
        id: 'email',
        type: 'email',
        required: true,
        labelKey: 'saveDialog.labelEmail',
        placeholderKey: 'saveDialog.placeholderEmail',
        visibility: ['email', 'test-drive', 'callback'],
      },
      {
        id: 'phone',
        type: 'phone',
        required: true,
        labelKey: 'saveDialog.labelMobileNumber',
        placeholderKey: 'saveDialog.placeholderMobileNumber',
        visibility: ['whatsapp', 'test-drive', 'callback'],
      },
      {
        id: 'model',
        type: 'select',
        required: true,
        labelKey: 'saveDialog.labelModel',
        placeholderKey: 'saveDialog.labelModel',
        optionsSource: 'models',
        visibility: ['test-drive', 'callback'],
      },
      {
        id: 'dealer',
        type: 'select',
        required: true,
        labelKey: 'saveDialog.labelDealership',
        placeholderKey: 'saveDialog.placeholderSelectDealership',
        optionsSource: 'dealers',
        visibility: [
          'test-drive',
          'callback',
          'whatsapp',
          'email',
        ],
        // 1.0's save forms show the dealership dropdown but do not gate
        // submission on it; the booking forms and Save to WhatsApp
        // require it (GM needs a dealer on WhatsApp leads); Email stays optional.
        requiredFor: [
          'test-drive',
          'callback',
          'whatsapp',
        ],
      },
      {
        id: 'testDriveDate',
        type: 'date',
        required: true,
        labelKey: 'saveDialog.labelTestDriveDate',
        placeholderKey: 'saveDialog.placeholderTestDriveDate',
        visibility: [
          'test-drive',
        ],
      },
      {
        id: 'testDriveTime',
        type: 'time',
        required: true,
        labelKey: 'saveDialog.labelTestDriveTime',
        placeholderKey: 'saveDialog.placeholderTestDriveTime',
        visibility: [
          'test-drive',
        ],
      },
      {
        id: 'comments',
        type: 'textarea',
        required: false,
        labelKey: 'saveDialog.labelComments',
        placeholderKey: 'saveDialog.placeholderComments',
        visibility: ['test-drive', 'callback'],
      },
    ],
  },
  externalScript: {
    dev: {
      head: {
        links: [
          {
            snippet: 'https://assets.adobedtm.com/ea8c57fea068/636ca1404ad1/launch-bea0833819b4-development.min.js',
            iframe: true,
            standalone: true
          },
          {
            snippet: 'https://assets.gm.com/responsive-iframe.min.js',
            iframe: true,
            standalone: true
          },
          {
            snippet: 'https://cmp.osano.com/3j6afV3OqT/4661232f-0324-44a1-bbfd-07fe470494c7/osano.js',
            iframe: false,
            standalone: true
          }
        ],
        snippets: [
          {
            snippet: '<!-- Meta Pixel Code -->\n<script>\n!function(f,b,e,v,n,t,s)\n{if(f.fbq)return;n=f.fbq=function(){n.callMethod?\nn.callMethod.apply(n,arguments):n.queue.push(argum\nents)};\nif(!f._fbq)f._fbq=n;n.push=n;n.loaded=!0;n.version=\'2.0\';\nn.queue=[];t=b.createElement(e);t.async=!0;\nt.src=v;s=b.getElementsByTagName(e)[0];\ns.parentNode.insertBefore(t,s)}(window,\ndocument,\'script\',\n\'https://connect.facebook.net/en_US/fbevents.js\');\nfbq(\'init\', \'2090290248171811\');\nfbq(\'track\', \'PageView\');\n</script>\n<noscript><img height="1" width="1"\nstyle="display:none"\nsrc="https://www.facebook.com/tr?\nid=2090290248171811&ev=PageView&noscript=1"\n/></noscript>\n<!-- End Meta Pixel Code -->\n        ',
            iframe: true,
            standalone: true
          },
          {
            snippet: '<!-- Google tag (gtag.js) -->\n<script async src="https://www.googletagmanager.com/gtag/js?id=AW-865363499"></script>\n<script>\nwindow.dataLayer = window.dataLayer || [];\nfunction gtag(){dataLayer.push(arguments);}\ngtag(\'js\', new Date());\ngtag(\'config\', \'AW-865363499\');\ngtag(\'config\', \'AW-780743336\');\ngtag(\'config\', \'AW-1050874867\');\ngtag(\'config\', \'AW-1033240730\');\n</script>\n        ',
            iframe: true,
            standalone: true
          }
        ]
      },
      body: {
        snippets: [
          {
            snippet: '<script type="text/javascript">_satellite.pageBottom();</script>',
            iframe: true,
            standalone: true,
            async: false
          }
        ]
      }
    },
    staging: {
      head: {
        links: [
          {
            snippet: 'https://assets.adobedtm.com/ea8c57fea068/636ca1404ad1/launch-85b05c76e071.min.js',
            iframe: true,
            standalone: true,
            async: false
          },
          {
            snippet: 'https://assets.gm.com/responsive-iframe.min.js',
            iframe: true,
            standalone: true
          },
          {
            snippet: 'https://cmp.osano.com/3j6afV3OqT/4661232f-0324-44a1-bbfd-07fe470494c7/osano.js',
            iframe: false,
            standalone: true
          }
        ],
        snippets: [
          {
            snippet: '<!-- Meta Pixel Code -->\n<script>\n!function(f,b,e,v,n,t,s)\n{if(f.fbq)return;n=f.fbq=function(){n.callMethod?\nn.callMethod.apply(n,arguments):n.queue.push(argum\nents)};\nif(!f._fbq)f._fbq=n;n.push=n;n.loaded=!0;n.version=\'2.0\';\nn.queue=[];t=b.createElement(e);t.async=!0;\nt.src=v;s=b.getElementsByTagName(e)[0];\ns.parentNode.insertBefore(t,s)}(window,\ndocument,\'script\',\n\'https://connect.facebook.net/en_US/fbevents.js\');\nfbq(\'init\', \'2090290248171811\');\nfbq(\'track\', \'PageView\');\n</script>\n<noscript><img height="1" width="1"\nstyle="display:none"\nsrc="https://www.facebook.com/tr?\nid=2090290248171811&ev=PageView&noscript=1"\n/></noscript>\n<!-- End Meta Pixel Code -->\n        ',
            iframe: true,
            standalone: true
          },
          {
            snippet: '<!-- Google tag (gtag.js) -->\n<script async src="https://www.googletagmanager.com/gtag/js?id=AW-865363499"></script>\n<script>\nwindow.dataLayer = window.dataLayer || [];\nfunction gtag(){dataLayer.push(arguments);}\ngtag(\'js\', new Date());\ngtag(\'config\', \'AW-865363499\');\ngtag(\'config\', \'AW-780743336\');\ngtag(\'config\', \'AW-1050874867\');\ngtag(\'config\', \'AW-1033240730\');\n</script>\n        ',
            iframe: true,
            standalone: true
          }
        ]
      },
      body: {
        snippets: [
          {
            snippet: '<script type="text/javascript">_satellite.pageBottom();</script>',
            iframe: true,
            standalone: true,
            async: false
          }
        ]
      }
    },
    main: {
      head: {
        links: [
          {
            snippet: 'https://assets.adobedtm.com/ea8c57fea068/636ca1404ad1/launch-85b05c76e071.min.js',
            iframe: true,
            standalone: true,
            async: false
          },
          {
            snippet: 'https://assets.gm.com/responsive-iframe.min.js',
            iframe: true,
            standalone: true
          },
          {
            snippet: 'https://cmp.osano.com/3j6afV3OqT/4661232f-0324-44a1-bbfd-07fe470494c7/osano.js',
            iframe: false,
            standalone: true
          }
        ],
        snippets: [
          {
            snippet: '<!-- Meta Pixel Code -->\n<script>\n!function(f,b,e,v,n,t,s)\n{if(f.fbq)return;n=f.fbq=function(){n.callMethod?\nn.callMethod.apply(n,arguments):n.queue.push(argum\nents)};\nif(!f._fbq)f._fbq=n;n.push=n;n.loaded=!0;n.version=\'2.0\';\nn.queue=[];t=b.createElement(e);t.async=!0;\nt.src=v;s=b.getElementsByTagName(e)[0];\ns.parentNode.insertBefore(t,s)}(window,\ndocument,\'script\',\n\'https://connect.facebook.net/en_US/fbevents.js\');\nfbq(\'init\', \'2090290248171811\');\nfbq(\'track\', \'PageView\');\n</script>\n<noscript><img height="1" width="1"\nstyle="display:none"\nsrc="https://www.facebook.com/tr?\nid=2090290248171811&ev=PageView&noscript=1"\n/></noscript>\n<!-- End Meta Pixel Code -->\n        ',
            iframe: true,
            standalone: true
          },
          {
            snippet: '<!-- Google tag (gtag.js) -->\n<script async src="https://www.googletagmanager.com/gtag/js?id=AW-865363499"></script>\n<script>\nwindow.dataLayer = window.dataLayer || [];\nfunction gtag(){dataLayer.push(arguments);}\ngtag(\'js\', new Date());\ngtag(\'config\', \'AW-865363499\');\ngtag(\'config\', \'AW-780743336\');\ngtag(\'config\', \'AW-1050874867\');\ngtag(\'config\', \'AW-1033240730\');\n</script>\n        ',
            iframe: true,
            standalone: true
          }
        ]
      },
      body: {
        snippets: [
          {
            snippet: '<script type="text/javascript">_satellite.pageBottom();</script>',
            iframe: true,
            standalone: true,
            async: false
          }
        ]
      }
    }
  },
  filterOptions: [
    {
      id: 'modelyear',
      key: 'modelyear',
      label: {
        en: 'Model Year',
        ar: 'سنة الطراز'
      },
      disabled: true,
      type: 'chip',
      options: [
        {
          id: '2025',
          label: {
            en: '2025',
            ar: '2025'
          },
          value: '2025',
          checked: false
        },
        {
          id: '2026',
          label: {
            en: '2026',
            ar: '2026'
          },
          value: '2026',
          checked: true
        }
      ]
    }
  ],
  styleConfig: {
    cssVariables: {
      '--default-font-family': 'Chevy Sans',
      '--bold-font-family': 'Chevy Sans Medium',
    },
  },
} as unknown as UIMetadataComplete;
